import type { Client as TypesenseClient } from 'typesense'
import { algoliaFilterToTypesense } from './algoliaFilterToTypesense'

/**
 * Typesense implementation of the parts of the Algolia client API this app uses.
 *
 * The rest of the app still speaks "Algolia": index names (incl. sort replicas),
 * `filters` strings and Algolia-shaped responses (hits, nbHits, page, ...).
 * This module maps those onto Typesense collections, presets and filter_by syntax.
 *
 * Contract (algolia-sync-checker/docs/typesense-frontti.md): the front only uses the fixed
 * collection aliases and preset names below. Search tuning (query_by, weights, typos, sorting,
 * curations) lives in the presets, so the front never sends those parameters.
 */

export type SearchKind = 'products' | 'categories' | 'contents' | 'blogs' | 'spareparts'

export interface TypesenseNode {
  host: string
  port: number
  protocol: string
}

export interface TypesenseSearchConfig {
  /** Typesense Cloud load balancer (HA cluster); the individual nodes are its fallbacks */
  nearestNode?: TypesenseNode | null
  nodes: TypesenseNode[]
  /** Search-only key (documents:search), may be exposed to the browser */
  apiKey: string
}

/** Algolia index names used by the app, for mapping them onto Typesense collections */
export interface AlgoliaIndexNames {
  products: string
  categories: string
  contents: string
  blogs: string
}

export interface AlgoliaLikeRequestOptions {
  filters?: string
  /** false = bypass the result cache (cart, checkout: fresh price and stock) */
  cacheable?: boolean
  hitsPerPage?: number
  page?: number
  distinct?: boolean
  attributesToRetrieve?: string[]
  ruleContexts?: string[]
  [key: string]: unknown
}

export interface AlgoliaLikeResponse<T = any> {
  hits: T[]
  nbHits: number
  page: number
  nbPages: number
  hitsPerPage: number
  query: string
  params: string
  processingTimeMS: number
  exhaustiveNbHits: boolean
}

/** Collection aliases. Versioned collections (tarvike_products_v5 …) are internal to the backend. */
export const COLLECTIONS: Record<SearchKind, string> = {
  products: 'tarvike_products',
  categories: 'tarvike_categories',
  contents: 'tarvike_contents',
  blogs: 'tarvike_blogs',
  spareparts: 'tarvike_spareparts'
}

/** Each collection has a preset of the same name */
export const PRESETS: Record<SearchKind, string> = { ...COLLECTIONS }

/** The app uses the collection names as index names; the Algolia-era names are only kept for the tests */
export const DEFAULT_INDEX_NAMES: AlgoliaIndexNames = {
  products: COLLECTIONS.products,
  categories: COLLECTIONS.categories,
  contents: COLLECTIONS.contents,
  blogs: COLLECTIONS.blogs
}

/**
 * Algolia sort replicas (index name affix). The sort preset is the products preset + the same affix,
 * e.g. tarvike_products_replica_sort_newest. It contains the order and enable_curations=false.
 */
export const SORT_REPLICA_AFFIXES = [
  '_replica_sort_newest',
  '_replica_sort_name_default_asc',
  '_replica_sort_name_default_desc',
  '_replica_sort_finalprice_asc',
  '_replica_sort_finalprice_desc',
  '_replica_sort_reviews_desc'
]

/** Results are cached briefly per process (SSR) and per page load (browser) to spare the cluster from repeats */
export const SEARCH_CACHE_SECONDS = 60
export const CONNECTION_TIMEOUT_SECONDS = 10
const MAX_PER_PAGE = 250

/**
 * Only these Algolia ruleContexts are sent as Typesense curation_tags. Sending any tag makes Typesense
 * skip every untagged (query-based) curation rule, and the Algolia-era contexts like "product_search"
 * have no Typesense rules, so passing them would just disable the query rules.
 */
const CURATION_TAG_PREFIXES = ['kategoria-']

export function curationTags(ruleContexts?: string[]) {
  const tags = (ruleContexts || []).filter(tag => CURATION_TAG_PREFIXES.some(prefix => tag.startsWith(prefix)))
  return tags.length ? tags : undefined
}

/**
 * Large fields not needed in listings. Excluded (instead of using include_fields) because
 * Typesense include_fields on a nested object only returns the sub-fields declared in the schema.
 */
export const LISTING_EXCLUDE_FIELDS: Partial<Record<SearchKind, string>> = {
  products: 'description,meta,nimiTarkka,nameParts,attachments,sizeCharts,technicalSpecificationsString,video,relatedProducts,synonymProducts,shippingCosts',
  categories: 'description',
  contents: 'content',
  blogs: 'content,excerpt'
}

export interface ResolvedIndex {
  kind: SearchKind
  collection: string
  preset: string
}

export function createIndexResolver(algoliaIndexes: AlgoliaIndexNames) {
  const build = (kind: SearchKind, preset = PRESETS[kind]): ResolvedIndex => ({ kind, collection: COLLECTIONS[kind], preset })

  return (indexName: string): ResolvedIndex => {
    for (const kind of ['categories', 'contents', 'blogs', 'spareparts'] as const) {
      if (indexName === COLLECTIONS[kind] || (kind !== 'spareparts' && indexName === algoliaIndexes[kind])) return build(kind)
    }
    if (indexName === COLLECTIONS.products) return build('products')
    if (algoliaIndexes.products && indexName.startsWith(algoliaIndexes.products)) {
      const affix = indexName.slice(algoliaIndexes.products.length)
      if (!affix) return build('products')
      if (SORT_REPLICA_AFFIXES.includes(affix)) return build('products', PRESETS.products + affix)
      console.warn(`[typesense] Unknown sort replica "${indexName}", using default ranking`)
      return build('products')
    }

    console.warn(`[typesense] Unknown index "${indexName}", falling back to products collection`)
    return build('products')
  }
}

function toAlgoliaHit(kind: SearchKind, document: Record<string, any>) {
  const hit = { ...document, objectID: document.objectID ?? document.id }
  // Algolia category records use the numeric category id as `id`
  if (kind === 'categories' && document.categoryId !== undefined) hit.id = document.categoryId
  return hit
}

function hitsFromResult(kind: SearchKind, result: any) {
  const hits = result.grouped_hits
    ? result.grouped_hits.map((group: any) => group.hits[0])
    : result.hits || []
  return hits.map((hit: any) => toAlgoliaHit(kind, hit.document))
}

export interface SearchRequest {
  indexName: string
  query?: string
  options?: AlgoliaLikeRequestOptions
}

export function createTypesenseSearch(config: TypesenseSearchConfig, algoliaIndexes: AlgoliaIndexNames = DEFAULT_INDEX_NAMES) {
  // The Typesense client (with axios ~130 kB) is loaded on the first search, not with every page in the browser
  // (mobiilinopeus 27.10.2026). Every search here is async anyway.
  let clients: Promise<{ cached: TypesenseClient, uncached: TypesenseClient }> | null = null
  const getClient = ({ cached = true } = {}) => {
    clients ??= import('./typesenseClient').then(({ createTypesenseClient }) => ({
      cached: createTypesenseClient(config),
      uncached: createTypesenseClient(config, { cache: false })
    }))
    // A failed chunk load (e.g. offline) may be retried on the next search
    clients.catch(() => {
      clients = null
    })
    return clients.then(c => (cached ? c.cached : c.uncached))
  }
  const resolveIndex = createIndexResolver(algoliaIndexes)

  /** Algolia request -> Typesense search parameters (without paging) */
  function buildSearch(indexName: string, query = '', options: AlgoliaLikeRequestOptions = {}) {
    const target = resolveIndex(indexName)
    const filterBy = algoliaFilterToTypesense(options.filters)
    const tags = curationTags(options.ruleContexts)

    const params: Record<string, unknown> = {
      collection: target.collection,
      preset: target.preset,
      q: query || '*',
      ...(filterBy && { filter_by: filterBy }),
      ...(tags && { curation_tags: tags.join(',') }),
      ...(options.attributesToRetrieve && LISTING_EXCLUDE_FIELDS[target.kind] && { exclude_fields: LISTING_EXCLUDE_FIELDS[target.kind] }),
      // Equivalent of Algolia distinct (attributeForDistinct: parentSku)
      ...(options.distinct && target.kind === 'products' && { group_by: 'parentSku', group_limit: 1, group_missing_values: false })
    }
    return { target, params, filterBy }
  }

  function toResponse<T>(kind: SearchKind, results: any[], query: string, page: number, hitsPerPage: number, sliceFrom?: number): AlgoliaLikeResponse<T> {
    const found: number = results[0]?.found ?? 0
    let hits = results.flatMap(result => hitsFromResult(kind, result))
    if (sliceFrom !== undefined) hits = hits.slice(sliceFrom, sliceFrom + hitsPerPage)
    return {
      hits,
      nbHits: found,
      page,
      nbPages: Math.ceil(found / hitsPerPage),
      hitsPerPage,
      query,
      params: '',
      processingTimeMS: results[0]?.search_time_ms ?? 0,
      exhaustiveNbHits: true
    }
  }

  const emptyResponse = <T>(query: string, page: number, hitsPerPage: number): AlgoliaLikeResponse<T> => ({
    hits: [], nbHits: 0, page, nbPages: 0, hitsPerPage, query, params: '', processingTimeMS: 0, exhaustiveNbHits: true
  })

  async function search<T = any>(indexName: string, query = '', options: AlgoliaLikeRequestOptions = {}): Promise<AlgoliaLikeResponse<T>> {
    const { target, params, filterBy } = buildSearch(indexName, query, options)
    const hitsPerPage = options.hitsPerPage ?? 20
    const page = options.page ?? 0

    const runSearch = async (tsPage: number, perPage: number) => {
      const searchClient = await getClient({ cached: options.cacheable !== false })
      const { results } = await searchClient.multiSearch.perform({ searches: [{ ...params, page: tsPage, per_page: perPage }] } as any)
      const result = results[0] as any
      if (result.error) throw new Error(`${result.code}: ${result.error}`)
      return result
    }

    try {
      // Typesense caps per_page at 250: fetch larger Algolia pages in 250-hit chunks
      const start = page * hitsPerPage
      const perPage = Math.min(hitsPerPage, MAX_PER_PAGE)
      const firstPage = hitsPerPage <= MAX_PER_PAGE ? page + 1 : Math.floor(start / MAX_PER_PAGE) + 1
      const lastPage = hitsPerPage <= MAX_PER_PAGE ? firstPage : Math.ceil((start + hitsPerPage) / MAX_PER_PAGE)

      const first = await runSearch(firstPage, perPage)
      const lastAvailablePage = Math.min(lastPage, Math.ceil((first.found ?? 0) / perPage))
      const rest = await Promise.all(
        Array.from({ length: Math.max(0, lastAvailablePage - firstPage) }, (_, i) => runSearch(firstPage + 1 + i, perPage))
      )

      const sliceFrom = hitsPerPage > MAX_PER_PAGE ? start - (firstPage - 1) * MAX_PER_PAGE : undefined
      return toResponse<T>(target.kind, [first, ...rest], query, page, hitsPerPage, sliceFrom)
    }
    catch (error) {
      console.warn(`[typesense] Search failed on "${target.collection}" (filter_by: ${filterBy || '-'})`, error)
      return emptyResponse<T>(query, page, hitsPerPage)
    }
  }

  /**
   * Several searches in one multi_search request (e.g. the quick search panel: products, categories,
   * contents and blogs). Up to 250 hits per search. A failed search returns an empty response.
   */
  async function multiSearch(requests: SearchRequest[]): Promise<AlgoliaLikeResponse[]> {
    const built = requests.map(({ indexName, query = '', options = {} }) => {
      const hitsPerPage = Math.min(options.hitsPerPage ?? 20, MAX_PER_PAGE)
      const page = options.page ?? 0
      return { ...buildSearch(indexName, query, options), query, hitsPerPage, page }
    })

    try {
      const client = await getClient()
      const { results } = await client.multiSearch.perform({
        searches: built.map(b => ({ ...b.params, page: b.page + 1, per_page: b.hitsPerPage }))
      } as any)
      return built.map((b, i) => {
        const result = (results as any[])[i]
        if (result?.error) {
          console.warn(`[typesense] Search failed on "${b.target.collection}": ${result.code}: ${result.error}`)
          return emptyResponse(b.query, b.page, b.hitsPerPage)
        }
        return toResponse(b.target.kind, [result], b.query, b.page, b.hitsPerPage)
      })
    }
    catch (error) {
      console.warn('[typesense] Multi search failed', error)
      return built.map(b => emptyResponse(b.query, b.page, b.hitsPerPage))
    }
  }

  return {
    search,
    multiSearch,
    /** Algolia-compatible `client.initIndex(name).search(query, options)` */
    initIndex: (indexName: string) => ({
      search: <T = any>(query: string, options?: AlgoliaLikeRequestOptions) => search<T>(indexName, query, options)
    })
  }
}

/**
 * The adapter adds to every hit a copy of the raw Typesense hit and highlight/snippet objects for
 * every field. Nothing in the site reads them, but on server-rendered listings they were sent with
 * the page (the category page state doubled in size). Highlights are kept if the search asks for them.
 */
export function stripHitExtras(hits: Record<string, unknown>[] | undefined, params?: { attributesToHighlight?: string[] }) {
  const keepHighlights = !Array.isArray(params?.attributesToHighlight) || params.attributesToHighlight.length > 0
  hits?.forEach((hit) => {
    delete hit._rawTypesenseHit
    delete hit._snippetResult
    if (!keepHighlights) delete hit._highlightResult
  })
}
